import { downloadBlob } from "components/preview/downloadBlob";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useCallback, useState } from "react";
import { formatApiError } from "utils/formatApiError";

import {
  CONFIG_EXPORT_FORMATS,
  type ConfigExportFormat,
} from "./configExportFormats";
import { useLazyExportIntegrationConfigQuery } from "./integrationsBeta";

/** `busy` value while the whole config (not one field) is being exported. */
export const CONFIG_EXPORT_TARGET = "config";

/**
 * Download the SAVED config of an integration (whole, or one field) as a
 * file: the server builds it from the persisted config, so staged edits are
 * never included - callers say so next to the action.
 */
export function useConfigExport(integration: { id: number; slug: string }) {
  const dispatch = useAppDispatch();
  const [trigger] = useLazyExportIntegrationConfigQuery();
  /** The field key being exported, CONFIG_EXPORT_TARGET for the whole config. */
  const [busy, setBusy] = useState<string | null>(null);

  const exportConfig = useCallback(
    async (format: ConfigExportFormat, field?: string) => {
      const target = field ?? CONFIG_EXPORT_TARGET;
      setBusy(target);
      try {
        const { blob, filename } = await trigger({
          integrationId: integration.id,
          format,
          field,
        }).unwrap();
        downloadBlob(
          blob,
          filename ??
            `${integration.slug}_${target}.${CONFIG_EXPORT_FORMATS[format].ext}`,
        );
      } catch (err) {
        dispatch(
          pushNotification({
            severity: "error",
            message: formatApiError(
              (err as { data?: unknown })?.data,
              "Could not export the configuration.",
            ),
          }),
        );
      } finally {
        setBusy(null);
      }
    },
    [dispatch, integration.id, integration.slug, trigger],
  );

  return { exportConfig, busy };
}
